import { Alert, Box, InputAdornment } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { GetProfileMedicationsDtoFormEnum } from 'woofwoof-api';
import { RHFCheckbox, RHFTextField } from 'src/components/hook-form';
import { translateKey } from 'src/locales/translate';
import { unitLabelsFor } from 'src/sections/@dashboard/warehouse/utils/clinicalStock';
import { unitLabel } from 'src/sections/@dashboard/warehouse/utils/recordCardFormat';
import DosageAutofillAlert from './DosageAutofillAlert';
import MedicationEnumField from './MedicationEnumField';
import { DosageAutofillNotice } from './dosageAutofill';
import { stockUnitForDoseForm } from './medicationDrugData';
import { SPAN_FULL, SPAN_WIDE } from './medicationFormLayout';
import {
  DURATIONS_WITH_VALUE,
  FREQUENCIES_WITH_VALUE,
  MEDICATION_DURATION_OPTIONS,
  MEDICATION_FORM_OPTIONS,
  MEDICATION_FREQUENCY_OPTIONS,
  MEDICATION_STRENGTH_UNIT_OPTIONS,
} from './medicationFormOptions';

interface MedicationDosageSectionProps {
  disabled?: boolean;
  /** #692: where the prefilled dose came from; null when nothing was prefilled. */
  autofillNotice?: DosageAutofillNotice | null;
  /** #692: "Wyczyść podpowiedź". */
  onClearAutofill?: () => void;
}


/**
 * How much, in what, how often and for how long. Required unless the vet ticks
 * "dawka do ustalenia" — the case where a medication is attached at a treatment
 * before the patient has been weighed.
 */
export default function MedicationDosageSection({
  disabled,
  autofillNotice = null,
  onClearAutofill,
}: MedicationDosageSectionProps) {
  const { watch } = useFormContext();
  const dosagePending = Boolean(watch('dosagePending'));
  const frequencyType = watch('frequencyType');
  const durationType = watch('durationType');
  const doseForm: GetProfileMedicationsDtoFormEnum | '' = watch('form');
  // P7B-R11: the Warehouse reads the dose in the form's own unit, so the
  // field says it — a dose typed in mg would be taken as tablets or ml.
  const doseUnit = stockUnitForDoseForm(doseForm);

  const required = !dosagePending;

  return (
    <>
      {autofillNotice && onClearAutofill && (
        <DosageAutofillAlert
          notice={autofillNotice}
          onClear={onClearAutofill}
          disabled={disabled}
        />
      )}
      <Box sx={SPAN_FULL}>
        <RHFCheckbox
          name="dosagePending"
          label={translateKey('medication.form.dosagePending')}
          disabled={disabled}
        />
        {dosagePending && (
          <Alert severity="info" sx={{ mt: 1 }}>
            {translateKey('medication.form.dosagePendingHint')}
          </Alert>
        )}
      </Box>

      <RHFTextField
        name="strengthValue"
        label={translateKey('medication.form.strengthValue')}
        type="number"
        required={required}
        disabled={disabled}
        inputProps={{ min: 0, step: 0.01 }}
      />

      <MedicationEnumField
        name="strengthUnit"
        customName="strengthUnitCustom"
        label={translateKey('medication.form.strengthUnit')}
        customLabel={translateKey('medication.form.strengthUnitCustom')}
        options={MEDICATION_STRENGTH_UNIT_OPTIONS}
        required={required}
        disabled={disabled}
      />

      <MedicationEnumField
        name="form"
        customName="formCustom"
        label={translateKey('medication.form.medicationForm')}
        customLabel={translateKey('medication.form.medicationFormCustom')}
        options={MEDICATION_FORM_OPTIONS}
        required={required}
        disabled={disabled}
      />

      <RHFTextField
        name="quantityValue"
        label={translateKey('medication.form.quantityValue')}
        type="number"
        required={required}
        disabled={disabled}
        inputProps={{ min: 0, step: 0.25 }}
        InputProps={
          doseUnit
            ? {
                endAdornment: (
                  <InputAdornment
                    position="end"
                    data-testid="medication-quantity-unit"
                  >
                    {unitLabel(doseUnit, null, translateKey)}
                  </InputAdornment>
                ),
              }
            : undefined
        }
        helperText={
          doseUnit
            ? translateKey('medication.form.quantityHintUnit', {
                unit: unitLabelsFor({ unit: doseUnit }).many,
              })
            : translateKey('medication.form.quantityHint')
        }
      />

      <MedicationEnumField
        name="frequencyType"
        customName="frequencyCustom"
        label={translateKey('medication.form.frequencyType')}
        customLabel={translateKey('medication.form.frequencyCustom')}
        options={MEDICATION_FREQUENCY_OPTIONS}
        required={required}
        disabled={disabled}
      />

      {FREQUENCIES_WITH_VALUE.includes(frequencyType) && (
        <RHFTextField
          name="frequencyValue"
          label={translateKey('medication.form.frequencyValue')}
          type="number"
          required
          disabled={disabled}
          inputProps={{ min: 1, step: 1 }}
        />
      )}

      <MedicationEnumField
        name="durationType"
        customName="durationCustom"
        label={translateKey('medication.form.durationType')}
        customLabel={translateKey('medication.form.durationCustom')}
        options={MEDICATION_DURATION_OPTIONS}
        required={required}
        disabled={disabled}
      />

      {DURATIONS_WITH_VALUE.includes(durationType) && (
        <RHFTextField
          name="durationValue"
          label={translateKey('medication.form.durationValue')}
          type="number"
          required
          disabled={disabled}
          inputProps={{ min: 1, step: 1 }}
        />
      )}

      <Box sx={SPAN_WIDE}>
        <RHFTextField
          name="dosageSchedule"
          label={translateKey('medication.form.dosageSchedule')}
          multiline
          rows={2}
          disabled={disabled}
          helperText={translateKey('medication.form.dosageScheduleHint')}
          fullWidth
        />
      </Box>
    </>
  );
}
